/* eslint-disable react/jsx-curly-newline */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import { useParams, Redirect } from 'react-router-dom';
import Helmet from 'react-helmet';

import useStyles from './styles';

import ActionsCourse from '../../../store/ducks/course';

import TopBar from '../TopBar';
import SideMenu from '../SideMenu';
import NavigationBar from './NavigationBar';
import Locked from '../Locked';

import Media from './Media';
import Description from './Description';
import Files from './Files';
import Notes from './Notes';
import Questions from './Questions';
import Comments from './Comments';
import GoNext from './GoNext';
import ErrorMessage from './ErrorMessage';

const Lesson = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { course: courseId, lesson: lessonId } = useParams();
  const {
    list: coursesObj,
    error: { list: listCoursesError, current: currentCourseError },
    current: courseActive,
  } = useSelector(state => state.course);
  const [courseInfos, setCourseInfos] = useState({});
  const [lessonInfos, setLessonInfos] = useState({});
  const [loading, setLoading] = useState(true);
  const [courseRequesting, setCourseRequesting] = useState(false);
  const [courseLocked, setCourseLocked] = useState(false);
  const [courseEmpty, setCourseEmpty] = useState(false);
  const [lessonNotFound, setLessonNotFound] = useState(false);
  const [courseError, setCourseError] = useState(false);
  const [showSideMenu, setShowSideMenu] = useState(true);
  const [showNavigationBar, setShowNavigationBar] = useState(false);
  const [originFinishLesson, setOriginFinishLesson] = useState(null);
  const [redirectLesson, setRedirectLesson] = useState(false);
  const refScrollable = useRef(null);
  const [configSections, setConfigSections] = useState({
    content: {
      id: 'content',
      title: 'Conteúdo',
      height: 0,
      render: true,
    },
    files: {
      id: 'files',
      title: 'Arquivos',
      height: 0,
      render: false,
    },
    questions: {
      id: 'questions',
      title: 'Questionário',
      height: 0,
      render: false,
    },
    notes: {
      id: 'notes',
      title: 'Anotações',
      height: 0,
      render: true,
    },
    comments: {
      id: 'comments',
      title: 'Comentários',
      height: 0,
      render: true,
    },
  });

  const handleToggleSideMenu = useCallback((isVisible = null) => {
    setShowSideMenu(oldShowSideMenu =>
      typeof isVisible === 'boolean' ? isVisible : !oldShowSideMenu,
    );
  }, []);

  useEffect(
    () => () => {
      dispatch(ActionsCourse.setActiveCourse(null));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(ActionsCourse.registerLessonAccessed(lessonId));
  }, [dispatch, lessonId]);

  useEffect(() => {
    setLoading(true);
    setCourseEmpty(false);
    setLessonNotFound(false);
    setRedirectLesson(false);
  }, [courseActive]);

  // Get/set course details
  useEffect(() => {
    // Get course details
    const hasCourseInList = courseId in coursesObj;
    const hasErrorInCourses = !!listCoursesError || !!currentCourseError;
    const courseNotLoadedInList = !hasCourseInList && !hasErrorInCourses;
    const courseDetailed = coursesObj[courseId]?.courseDetailed;
    if (
      (courseNotLoadedInList || !courseDetailed) &&
      !hasErrorInCourses &&
      !courseRequesting
    ) {
      dispatch(ActionsCourse.requestCourse(courseId));
      setCourseRequesting(true);
      setLoading(true);
    }
    // Set lesson not found because error
    if (hasErrorInCourses) {
      setCourseError(true);
      setLoading(false);
      if (courseRequesting) setCourseRequesting(false);
    } else {
      setCourseError(false);
    }
    // Set course details
    if (hasCourseInList && !hasErrorInCourses) {
      const course = coursesObj[courseId];
      setCourseInfos(course);

      if (courseDetailed) {
        if (courseRequesting) setCourseRequesting(false);
        // Set course empty because lessons not found
        if (!Object.keys(course.lessons).length && !courseEmpty) {
          setCourseEmpty(true);
          setLoading(false);
        }
        // Set course not empty because lessons found
        if (Object.keys(course.lessons).length && courseEmpty) {
          setCourseEmpty(false);
        }
      }
    }
  }, [
    courseId,
    coursesObj,
    listCoursesError,
    currentCourseError,
    courseRequesting,
    courseEmpty,
    dispatch,
  ]);

  useEffect(() => {
    // Verify lesson changed and set loading
    if (courseInfos.id !== courseId || lessonInfos.id !== lessonId) {
      setLoading(true);
      setLessonNotFound(false);
    }
    // Set active course and lesson
    if (
      courseId !== courseActive?.course ||
      lessonId !== courseActive?.lesson
    ) {
      dispatch(ActionsCourse.setActiveCourse(courseId, lessonId));
      window.dispatchEvent(
        new CustomEvent('lessonChange', {
          detail: {
            courseId,
            lessonId,
          },
        }),
      );
    }
  }, [courseId, lessonId, courseInfos, courseActive, dispatch, lessonInfos.id]);

  // Get nextLesson if is not specified
  useEffect(() => {
    if (!lessonId && Object.keys(coursesObj).length) {
      const { nextLesson, conclusionPercent } = coursesObj[courseId];
      if (!nextLesson) {
        dispatch(ActionsCourse.requestNextLesson(courseId));
      } else if (nextLesson?.empty) {
        setCourseEmpty(true);
        setRedirectLesson(false);
        setLoading(false);
      } else {
        if (courseEmpty) setCourseEmpty(false);
        const certificateIsNext =
          nextLesson.completed &&
          nextLesson.certificate &&
          nextLesson.certificate?.daysToRelease <= 0 &&
          nextLesson.certificate?.conclusionPercent <= conclusionPercent;
        setRedirectLesson(certificateIsNext ? 'certificate' : nextLesson.id);
      }
    }
  }, [courseId, lessonId, coursesObj, dispatch, courseEmpty]);

  // Get/Set lesson details
  useEffect(() => {
    if (courseInfos.locked) {
      setCourseLocked(true);
    }
    if (lessonId && courseInfos.courseDetailed && !courseInfos.locked) {
      if (lessonId in courseInfos.lessons) {
        setLessonInfos(courseInfos.lessons[lessonId]);
      } else {
        setLessonInfos(false);
      }
    }
  }, [courseInfos, lessonId]);

  // Check if all ready to render NavigationBar
  useEffect(() => {
    const isLocked = lessonInfos && lessonInfos.daysToRelease > 0;
    setShowNavigationBar(lessonInfos && !isLocked);
    setOriginFinishLesson(null);
  }, [lessonInfos, loading]);

  const handleSectionConfig = useCallback((section, type, value) => {
    setConfigSections(oldConfigSections => ({
      ...oldConfigSections,
      [section]: {
        ...oldConfigSections[section],
        [type]: value,
      },
    }));
  }, []);

  const handleMidiaFinished = useCallback(() => {
    setOriginFinishLesson('media');
  }, []);

  return (
    <Box className={classes.container}>
      <Helmet>
        <title>
          {!lessonInfos.title && !courseInfos.name
            ? 'Buscando informações do curso...'
            : ''}
          {!lessonInfos.title && courseInfos.name ? courseInfos.name : ''}
          {lessonInfos.title
            ? `${lessonInfos.title} - ${courseInfos.name}`
            : ''}
        </title>
      </Helmet>
      <Box
        className={`content ${!showSideMenu && 'without-sidemenu'} ${
          courseEmpty && 'course-empty'
        }`}
      >
        {!courseLocked ? (
          <>
            {!courseEmpty && (
              <>
                <TopBar
                  handleToggleSideMenu={handleToggleSideMenu}
                  loading={loading}
                />
                <SideMenu
                  showSideMenu={showSideMenu}
                  handleToggleSideMenu={handleToggleSideMenu}
                  loading={loading}
                />
              </>
            )}
            {redirectLesson && (
              <Redirect to={`/course/${courseId}/${redirectLesson}`} />
            )}
            {lessonNotFound || courseEmpty || courseError ? (
              <ErrorMessage
                courseEmpty={courseEmpty}
                courseError={courseError}
              />
            ) : (
              <>
                <Box className="content-wrapper">
                  <Box
                    className={`content-scrollable ${
                      lessonInfos.daysToRelease > 0 ? 'flexible' : ''
                    }`}
                    ref={refScrollable}
                  >
                    {lessonInfos.daysToRelease > 0 ? (
                      <Locked daysToRelease={lessonInfos.daysToRelease} />
                    ) : (
                      <>
                        {lessonInfos.videoURL && (
                          <Media
                            lessonInfos={lessonInfos}
                            onMediaFinished={handleMidiaFinished}
                          />
                        )}
                        {showNavigationBar && (
                          <NavigationBar
                            sections={configSections}
                            hasMedia={!!lessonInfos.videoURL}
                            scrollContainer={refScrollable}
                            lesson={lessonId}
                          />
                        )}
                        <Description
                          onSectionChange={(type, value) =>
                            handleSectionConfig('content', type, value)
                          }
                        />
                        <Files
                          onSectionChange={(type, value) =>
                            handleSectionConfig('files', type, value)
                          }
                        />
                        <Questions
                          onSectionChange={(type, value) =>
                            handleSectionConfig('questions', type, value)
                          }
                        />
                        <Notes
                          onSectionChange={(type, value) =>
                            handleSectionConfig('notes', type, value)
                          }
                        />
                        <Comments
                          onSectionChange={(type, value) =>
                            handleSectionConfig('comments', type, value)
                          }
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </>
        ) : (
          <Redirect to="/not-found" />
        )}
      </Box>
      <GoNext lessonInfos={lessonInfos} origin={originFinishLesson} />
    </Box>
  );
};

export default Lesson;
